import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Send, Loader2, MessageSquare, User, Home as HomeIcon,
  Briefcase, Building2, Check, CheckCheck, X, UserPlus, VolumeX, Volume2, Reply, EyeOff,
} from 'lucide-react';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { formatDateTime } from './kit/time';
import { isSpendApprovalError } from './api';
import { errorMessage, publicLinkError } from './errors';
import { roleLabel } from './dealLabels';
import usePolling from './usePolling';
import AddDealPersonDialog from './AddDealPersonDialog';
import { Input } from '../components/ui/input';

// How often an open thread (staff or the borrower's public page) asks for new
// messages (usePolling pauses it while the tab is hidden).
const THREAD_POLL_MS = 30000;
// A poll answered with one of these will never succeed on its own (signed out,
// no longer allowed, deal gone, link turned off), so polling stops. Network errors and 5xx keep
// retrying.
const POLL_STOP_STATUSES = [401, 403, 404, 410];
// Within this many px of the bottom counts as "reading the latest message".
const NEAR_BOTTOM_PX = 80;

// Roles come from deal_messages_router._deal_staff_roles: flat-field roles
// (lo/agent/processor/assistant), staff slots, and — ADR-0006 — external
// slots (borrower, co_borrower, realtor, buyer_agent, seller_agent).
const HEADER_ROLE_ORDER = ['lo', 'agent', 'realtor', 'buyer_agent', 'seller_agent', 'processor', 'assistant', 'borrower', 'co_borrower', 'party'];
const HEADER_ROLE_LABELS = { lo: 'LO', agent: 'Realtor', realtor: 'Realtor', buyer_agent: 'Realtor', seller_agent: 'Realtor', processor: 'Processor', assistant: 'Assistant', borrower: 'Borrower', co_borrower: 'Borrower', party: 'Title / Escrow' };
// models.EXTERNAL_SLOTS — the only roles DELETE /participants/{user_id} can
// actually act on (assignments.remove_external only closes `kind: external`
// rows). lo/agent/processor/assistant come from flat fields, not assignment
// rows, so a remove action on them would always 404.
const EXTERNAL_SLOTS = ['borrower', 'co_borrower', 'realtor', 'buyer_agent', 'seller_agent', 'party'];

/**
 * Reusable message thread panel.
 * Works in two modes:
 *   - authed:   pass `apiClient` (axios instance already carrying JWT) + `dealId`
 *   - public:   pass `apiClient` (unauth axios) + `shareToken`
 */
export default function DealMessagesPanel({
  apiClient,
  dealId,       // for authed mode
  shareToken,   // for public borrower mode
  currentUserId,
  currentUserRole = 'lo',
  canRemoveParticipant = false, // loans.assign — DELETE /participants/{user_id} is gated on it too
  canAddParticipant = false,    // links.issue — POST /participants is gated on it too
  canMuteParticipant = false,   // loans.edit — POST /participants/{user_id}/mute is gated on it too
  canRemoveMessage = false,     // staff.manage (owner / admin) — POST /messages/{id}/remove is gated on it too
  navy = '#070B5F', green = '#6BA82E',
}) {
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState('');
  const [borrowerName, setBorrowerName] = useState('');
  const [borrowerUserId, setBorrowerUserId] = useState(null);
  const [hasReSide, setHasReSide] = useState(false);
  const [hasLoanSide, setHasLoanSide] = useState(false);
  const [mentionMenu, setMentionMenu] = useState(null);   // {top, left, query}
  const [pollStopped, setPollStopped] = useState(null);   // {detail} once polling gave up
  const [addOpen, setAddOpen] = useState(false);
  const [publicMuted, setPublicMuted] = useState(false);   // the link borrower's own mute (summary payload)
  const [replyTo, setReplyTo] = useState(null);           // the message being quoted in the draft
  const [postedMuted, setPostedMuted] = useState(false);   // a post just came back 403 `muted`
  const listRef = useRef(null);
  const composerRef = useRef(null);
  const stickToBottom = useRef(true);   // reader is at the bottom of the list
  const shownCount = useRef(0);
  const fetchSeq = useRef(0);
  const autoReadFor = useRef(null);     // dealId whose unread messages were marked read on open

  const isPublic = !!shareToken;
  // The borrower's page says a suspended workspace's link is paused (ticket 315).
  const failText = (e, fallback) => (isPublic ? publicLinkError(e, fallback) : errorMessage(e, fallback));
  // Public/borrower mode has no logged-in user, so `currentUserId` is never
  // passed by the caller. The summary payload carries the borrower's real
  // user_id (ADR-0006 — the lazy link account borrower_deal_router.py seats
  // on the deal) so @You highlighting and "is this my message" checks work.
  const effectiveUserId = isPublic ? borrowerUserId : currentUserId;
  // Staff paused this person's posting (ticket 68). They still read the thread.
  const selfMuted = postedMuted || (isPublic
    ? publicMuted
    : !!participants.find((p) => p.user_id === currentUserId)?.muted);

  // Same value in, same state out: a poll that finds nothing new must not
  // re-render the list (or fire the scroll effect below).
  const keepIfSame = (next) => (prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next);

  // Fetch the thread and apply it. Only `messages`, `participants` and the
  // public summary fields are written — never the draft or the @ picker — so
  // a background refresh can't wipe a half-typed reply. A newer fetch wins:
  // a poll that was in flight when you sent does not overwrite the result.
  const fetchThread = async () => {
    const seq = ++fetchSeq.current;
    if (isPublic) {
      const { data } = await apiClient.get(`/borrower/deal/${shareToken}`);
      if (seq !== fetchSeq.current) return;
      setMessages(keepIfSame(data.messages || []));
      setBorrowerName(data.borrower_name || '');
      setBorrowerUserId(data.borrower_user_id || null);
      setHasReSide(!!data.has_re_side);
      setHasLoanSide(!!data.has_loan_side);
      setPublicMuted(!!data.muted);
      if (!data.muted) setPostedMuted(false);
    } else {
      const [m, p] = await Promise.all([
        apiClient.get(`/deals/${dealId}/messages`),
        apiClient.get(`/deals/${dealId}/participants`),
      ]);
      if (seq !== fetchSeq.current) return;
      setMessages(keepIfSame(m.data.messages || []));
      setParticipants(keepIfSame(p.data.participants || []));
      if (!(p.data.participants || []).find((x) => x.user_id === currentUserId)?.muted) setPostedMuted(false);
      return m.data.unread || 0;
    }
    return 0;
  };

  // `markRead` is for the first load of a staff thread only: opening the thread
  // is reading it, so unread messages are marked read once, then the thread is
  // fetched again. A failure there is silent. Polls never do this.
  const load = async ({ markRead = false } = {}) => {
    try {
      const unread = await fetchThread();
      if (markRead && !isPublic && unread > 0 && autoReadFor.current !== dealId) {
        autoReadFor.current = dealId;
        try {
          await apiClient.post(`/deals/${dealId}/messages/read-all`);
          await fetchThread();
        } catch (e) { /* silent */ }
      }
    } catch (e) {
      toast.error(failText(e, 'Failed to load thread'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setPollStopped(null); setReplyTo(null); load({ markRead: true }); /* eslint-disable-next-line */ }, [dealId, shareToken]);

  // Ask for new messages every 30 s, on the staff thread and the borrower's
  // public page alike. Network errors and 5xx stay silent (the next tick
  // retries); a 401, 403, 404 or 410 stops the polling and leaves one line in
  // the panel.
  const pollThread = () => fetchThread().catch((e) => {
    if (POLL_STOP_STATUSES.includes(e?.response?.status)) {
      setPollStopped({ detail: e.response.data?.detail ? failText(e) : '' });
    }
  });
  usePolling(pollThread, THREAD_POLL_MS, { enabled: !!(dealId || shareToken) && !pollStopped, immediate: false });

  const onListScroll = () => {
    const el = listRef.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  // Follow the conversation to the bottom on first load, and when a message
  // arrives while the reader is already there. Someone reading older messages
  // keeps their place; sending a reply puts you back at the bottom (see post).
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const first = shownCount.current === 0;
    const grew = messages.length > shownCount.current;
    shownCount.current = messages.length;
    if (first || (grew && stickToBottom.current)) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      if (isPublic) {
        await apiClient.post(`/borrower/deal/${shareToken}/messages`, {
          body: text, borrower_name: borrowerName,
          ...(replyTo ? { reply_to: replyTo.message_id } : {}),
        });
      } else {
        await apiClient.post(`/deals/${dealId}/messages`, {
          body: text,
          ...(replyTo ? { reply_to: replyTo.message_id } : {}),
        });
      }
      setText('');
      setReplyTo(null);
      stickToBottom.current = true;
      await load();
    } catch (e) {
      // The spend-approval 403 already got its one toast from api.js.
      if (isSpendApprovalError(e)) return;
      if (e?.response?.data?.detail?.code === 'muted') setPostedMuted(true);
      toast.error(failText(e, 'Send failed'));
    } finally {
      setPosting(false);
    }
  };

  const markAllRead = async () => {
    if (isPublic) return;
    try {
      await apiClient.post(`/deals/${dealId}/messages/read-all`);
      load();
    } catch (e) { /* silent */ }
  };

  const removeParticipant = async (p) => {
    const ok = await confirmDialog({
      title: `Remove ${p.name}?`,
      description: `${p.name} loses access to this deal immediately. They can be added back later.`,
      confirmText: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/deals/${dealId}/participants/${p.user_id}`);
      toast.success(`Removed ${p.name}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Failed to remove participant');
    }
  };

  const removeMessage = async (m) => {
    const ok = await confirmDialog({
      title: 'Remove this message from view?',
      description: 'Everyone in the thread will see "Message removed by staff" instead of the text. The original stays in the stored record and the removal is logged.',
      confirmText: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiClient.post(`/deals/${dealId}/messages/${m.message_id}/remove`);
      if (replyTo?.message_id === m.message_id) setReplyTo(null);
      toast.success('Message removed from view');
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to remove message'));
    }
  };

  const toggleMute = async (p) => {
    const muting = !p.muted;
    const ok = await confirmDialog({
      title: muting ? `Pause ${p.name}'s messages?` : `Let ${p.name} post again?`,
      description: muting
        ? `${p.name} can still read this conversation but cannot post in it until you unmute them. Their link keeps working and other people on the deal are not affected.`
        : `${p.name} can post in this conversation again right away.`,
      confirmText: muting ? 'Mute' : 'Unmute',
      destructive: muting,
    });
    if (!ok) return;
    try {
      await apiClient.post(`/deals/${dealId}/participants/${p.user_id}/${muting ? 'mute' : 'unmute'}`);
      toast.success(muting ? `Muted ${p.name}` : `Unmuted ${p.name}`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, muting ? 'Failed to mute' : 'Failed to unmute'));
    }
  };

  // Computed once per render (not re-derived per keystroke inside the JSX
  // below) so the picker container's render condition and its `.map()` see
  // the same list — see handleComposerKeyDown for the callers that still
  // call `mentionOptions()` directly since they're outside the render path.
  const mentionOptionsList = mentionMenu ? mentionOptions() : [];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden flex flex-col" style={{ minHeight: 480 }} data-testid="deal-messages-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100 gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} style={{ color: navy }} />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Deal conversation</div>
            <div className="font-serif font-semibold text-sm" style={{ color: navy }}>
              {isPublic ? presentPublicRolesLabel() : `${presentRolesLabel()} (${participants.length} in this thread)`}
            </div>
          </div>
        </div>
        {!isPublic && (
          <div className="flex items-center gap-2">
            {canAddParticipant && (
              <button onClick={() => setAddOpen(true)} data-testid="btn-add-person"
                      className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
                <UserPlus size={12} /> Add a person
              </button>
            )}
            <button onClick={markAllRead} data-testid="btn-mark-read-all"
                    className="text-xs px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>
        )}
      </div>
      {canAddParticipant && !isPublic && (
        <AddDealPersonDialog apiClient={apiClient} dealId={dealId} open={addOpen} onOpenChange={setAddOpen} onAdded={() => load()} />
      )}

      {/* Participants strip (portal only) */}
      {!isPublic && participants.length > 0 && (
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 overflow-x-auto" data-testid="participants-strip">
          {participants.map((p) => (
            <div key={p.user_id} className="inline-flex items-center gap-1.5 shrink-0 px-2 py-1 rounded-full text-xs" style={{ background: '#f1f5f9' }}>
              <RoleIcon role={p.role} navy={navy} green={green} />
              <span className="font-medium" style={{ color: navy }}>{p.name}</span>
              <span className="text-[9px] uppercase tracking-wider text-slate-500">{p.label || roleLabel(p.role)}</span>
              {p.muted && (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }} data-testid={`muted-pill-${p.user_id}`}>
                  Muted
                </span>
              )}
              {canMuteParticipant && EXTERNAL_SLOTS.includes(p.role) && (
                <button
                  onClick={() => toggleMute(p)}
                  title={p.muted ? `Unmute ${p.name}` : `Mute ${p.name}`}
                  data-testid={`${p.muted ? 'unmute' : 'mute'}-participant-${p.user_id}`}
                  className="text-slate-400 hover:text-slate-700"
                >
                  {p.muted ? <Volume2 size={11} /> : <VolumeX size={11} />}
                </button>
              )}
              {canRemoveParticipant && EXTERNAL_SLOTS.includes(p.role) && (
                <button
                  onClick={() => removeParticipant(p)}
                  title={`Remove ${p.name}`}
                  data-testid={`remove-participant-${p.user_id}`}
                  className="text-slate-400 hover:text-red-600"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Message list */}
      <div ref={listRef} onScroll={onListScroll} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-list" style={{ maxHeight: 480 }}>
        {loading && (
          <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-slate-400 py-10 text-sm">
            <MessageSquare size={28} className="mx-auto text-slate-200 mb-2" />
            No messages yet. Start the conversation below.
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.message_id}
            m={m}
            isMine={m.sender_user_id === effectiveUserId || (isPublic && m.sender_role === 'borrower')}
            currentUserId={effectiveUserId}
            participants={participants}
            navy={navy}
            green={green}
            onReply={selfMuted ? null : setReplyTo}
            onRemove={canRemoveMessage && !isPublic ? removeMessage : null}
          />
        ))}
      </div>

      {pollStopped && (
        <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-500" role="status" data-testid="thread-stopped">
          <span>Messages stopped updating. Reload the page.</span>
          {pollStopped.detail && <span className="text-slate-400">{' '}{pollStopped.detail}</span>}
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-slate-100 p-3 relative">
        {isPublic && !borrowerName && (
          <Input
            type="text"
            value={borrowerName}
            onChange={(e) => setBorrowerName(e.target.value)}
            placeholder="Your name (once)"
            data-testid="msg-borrower-name"
            className="mb-2"
          />
        )}
        {mentionMenu && mentionOptionsList.length > 0 && (
          <div
            className="absolute bottom-full left-3 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-56 overflow-y-auto z-10 min-w-[220px]"
            data-testid="mention-picker"
          >
            {mentionOptionsList.map((p, i) => (
              <button
                key={p.user_id}
                onClick={() => insertMention(p)}
                onMouseEnter={() => setMentionMenu((prev) => (prev ? { ...prev, highlightedIndex: i } : prev))}
                data-testid={`mention-option-${p.user_id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                style={{ background: i === (mentionMenu.highlightedIndex || 0) ? '#f1f5f9' : undefined }}
              >
                <RoleIcon role={p.role} navy={navy} green={green} />
                <span className="font-medium" style={{ color: navy }}>{p.name}</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-500 ml-auto">{p.label || roleLabel(p.role)}</span>
              </button>
            ))}
          </div>
        )}
        {selfMuted ? (
          <div className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-3" role="status" data-testid="composer-muted">
            Posting in this thread has been paused by the loan team. You can still read it.
          </div>
        ) : (
        <>
        {replyTo && (
          <div className="mb-2 flex items-start gap-2 rounded-md bg-slate-50 border-l-2 px-3 py-1.5 text-xs text-slate-600" style={{ borderColor: green }} data-testid="reply-draft">
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: navy }}>Replying to {replyTo.sender_name}</div>
              <div className="truncate">{replyTo.removed ? 'Message removed by staff' : stripMentions(replyTo.body)}</div>
            </div>
            <button onClick={() => setReplyTo(null)} title="Cancel reply" data-testid="reply-cancel" className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            ref={composerRef}
            value={text}
            onChange={(e) => { setText(e.target.value); detectMention(e.target); }}
            onKeyDown={handleComposerKeyDown}
            placeholder={isPublic
              ? "Ask your loan officer or realtor anything. Use @ to tag someone. (Cmd/Ctrl+Enter to send)"
              : "Type a message. Use @ to tag someone. (Cmd/Ctrl+Enter to send)"}
            data-testid="msg-composer"
            rows={2}
            className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40 resize-none"
          />
          <button
            onClick={post}
            disabled={posting || !text.trim()}
            data-testid="msg-send"
            className="text-sm px-4 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
            style={{ background: green }}
          >
            {posting ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
            Send
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );

  // -----------------------------------------------------------------------
  // @mention helpers
  // -----------------------------------------------------------------------
  function detectMention(el) {
    const cursor = el.selectionStart;
    const beforeCursor = el.value.slice(0, cursor);
    const match = beforeCursor.match(/@([\w-]*)$/);
    if (match) {
      setMentionMenu({ query: match[1], highlightedIndex: 0 });
    } else {
      setMentionMenu(null);
    }
  }

  // Public/borrower mode has no staff roster to pick from (no `participants`
  // fetch — see `load()`). Scope mention targets to people who have already
  // posted in this thread: dedupe by sender_user_id, excluding the borrower's
  // own messages so they can't mention themselves.
  function publicMentionTargets() {
    const seen = new Set();
    const out = [];
    for (const m of messages) {
      if (!m.sender_user_id || m.sender_role === 'borrower') continue;
      if (seen.has(m.sender_user_id)) continue;
      seen.add(m.sender_user_id);
      out.push({ user_id: m.sender_user_id, role: m.sender_role, name: m.sender_name || m.sender_role || 'Someone' });
    }
    return out;
  }

  function presentRolesLabel() {
    const present = HEADER_ROLE_ORDER.filter((role) => participants.some((p) => p.role === role));
    return present.length ? present.map((role) => HEADER_ROLE_LABELS[role]).join(' · ') : 'Deal conversation';
  }

  // Public/borrower mode has no `participants` list to check against (see
  // `load()`), so it can't reuse `presentRolesLabel()` directly — instead it
  // uses the summary payload's has_re_side/has_loan_side flags, same source
  // BorrowerDealPortal.jsx uses to show/hide the Realtor/LO team cards.
  function presentPublicRolesLabel() {
    const labels = [];
    if (hasLoanSide) labels.push(HEADER_ROLE_LABELS.lo);
    if (hasReSide) labels.push(HEADER_ROLE_LABELS.agent);
    labels.push(HEADER_ROLE_LABELS.borrower);
    return labels.join(' · ');
  }

  function mentionOptions() {
    if (!mentionMenu) return [];
    const pool = isPublic ? publicMentionTargets() : participants;
    return pool
      .filter((p) => p.user_id !== effectiveUserId)
      .filter((p) => !mentionMenu.query || p.name.toLowerCase().includes(mentionMenu.query.toLowerCase()));
  }

  function handleComposerKeyDown(e) {
    if (mentionMenu) {
      const options = mentionOptions();
      if (e.key === 'ArrowDown' && options.length) {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          highlightedIndex: ((prev.highlightedIndex || 0) + 1) % options.length,
        }));
        return;
      }
      if (e.key === 'ArrowUp' && options.length) {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          highlightedIndex: ((prev.highlightedIndex || 0) - 1 + options.length) % options.length,
        }));
        return;
      }
      if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.shiftKey && options.length) {
        e.preventDefault();
        insertMention(options[mentionMenu.highlightedIndex || 0]);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); }
    if (e.key === 'Escape') setMentionMenu(null);
  }

  function insertMention(participant) {
    const el = composerRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const before = text.slice(0, cursor).replace(/@([\w-]*)$/, '');
    const after = text.slice(cursor);
    const token = `@[${participant.name}](${participant.user_id}) `;
    const newText = before + token + after;
    setText(newText);
    setMentionMenu(null);
    setTimeout(() => {
      el.focus();
      const pos = before.length + token.length;
      el.setSelectionRange(pos, pos);
    }, 10);
  }
}

function RoleIcon({ role, navy, green }) {
  const map = {
    lo:          { Icon: Briefcase, color: navy },
    agent:       { Icon: HomeIcon,  color: green },
    realtor:     { Icon: HomeIcon,  color: green },
    buyer_agent: { Icon: HomeIcon,  color: green },
    seller_agent: { Icon: HomeIcon, color: green },
    borrower:    { Icon: User,      color: '#f59e0b' },
    co_borrower: { Icon: User,      color: '#f59e0b' },
    processor:   { Icon: Building2, color: '#64748b' },
    assistant:   { Icon: Building2, color: '#64748b' },
    admin:       { Icon: Building2, color: navy },
    party:       { Icon: Building2, color: '#64748b' },
  };
  const { Icon, color } = map[role] || { Icon: User, color: '#64748b' };
  return <Icon size={11} style={{ color }} />;
}

function MessageBubble({ m, isMine, currentUserId, participants, navy, green, onReply, onRemove }) {
  const bubbleRole = roleLabel(m.sender_role);
  const bg = isMine ? navy : '#f1f5f9';
  const fg = isMine ? 'white' : '#0f172a';
  const roleColor = {
    lo: navy, agent: green, realtor: green, buyer_agent: green, seller_agent: green,
    borrower: '#f59e0b', co_borrower: '#f59e0b', processor: '#64748b', assistant: '#64748b', admin: navy, party: '#64748b',
  }[m.sender_role] || '#64748b';
  const rolePillColor = (isMine && roleColor === navy) ? green : roleColor;
  const mentionedMe = currentUserId && (m.mentions || []).includes(currentUserId);

  // Render body with @[Name](id) tokens as pills
  const rendered = renderMentions(m.body, participants, isMine, currentUserId);

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.message_id}`}>
      <div className="max-w-[75%]">
        <div className={`flex items-center gap-2 mb-1 ${isMine ? 'justify-end' : ''}`}>
          <span className="text-xs font-semibold" style={{ color: navy }}>{m.sender_name}</span>
          <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: rolePillColor }}>{bubbleRole}</span>
          {mentionedMe && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }} data-testid="mention-badge-me">
              @You
            </span>
          )}
          <span className="text-[10px] text-slate-400">{formatDateTime(m.created_at)}</span>
        </div>
        {m.removed ? (
          <div className="rounded-2xl px-4 py-2.5 text-sm italic text-slate-500 bg-slate-50 border border-dashed border-slate-200" data-testid={`msg-removed-${m.message_id}`}>
            Message removed by staff
          </div>
        ) : (
          <div className="rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap" style={{ background: bg, color: fg }}>
            {m.reply_preview && (
              <div className="mb-1.5 rounded-md px-2 py-1 text-xs border-l-2 whitespace-normal" style={{ background: isMine ? 'rgba(255,255,255,0.12)' : '#e2e8f0', borderColor: green }} data-testid={`reply-quote-${m.message_id}`}>
                <div className="font-semibold">{m.reply_preview.sender_name}</div>
                <div className="opacity-80">{m.reply_preview.removed ? 'Message removed by staff' : stripMentions(m.reply_preview.body)}</div>
              </div>
            )}
            {rendered}
          </div>
        )}
        {(onReply || (onRemove && !m.removed)) && (
          <div className={`mt-0.5 flex gap-3 text-[10px] text-slate-400 ${isMine ? 'justify-end' : ''}`}>
            {onReply && !m.removed && (
              <button onClick={() => onReply(m)} data-testid={`reply-${m.message_id}`} className="inline-flex items-center gap-1 hover:text-slate-700"><Reply size={10} /> Reply</button>
            )}
            {onRemove && !m.removed && (
              <button onClick={() => onRemove(m)} data-testid={`remove-msg-${m.message_id}`} className="inline-flex items-center gap-1 hover:text-red-600"><EyeOff size={10} /> Remove from view</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// "@[Name](id)" -> "@Name", for the one-line quote of an earlier message.
function stripMentions(body) {
  return (body || '').replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
}

function renderMentions(body, participants, isMine, currentUserId) {
  const parts = [];
  const re = /@\[([^\]]+)\]\(([^\)]+)\)/g;
  let lastIndex = 0;
  let match;
  let idx = 0;
  while ((match = re.exec(body)) !== null) {
    if (match.index > lastIndex) parts.push(body.slice(lastIndex, match.index));
    const name = match[1];
    const id = match[2];
    const isMe = id === currentUserId;
    parts.push(
      <span
        key={`m-${idx++}`}
        className="inline-flex items-center px-1.5 rounded font-semibold"
        style={{
          background: isMe ? '#fef3c7' : (isMine ? 'rgba(255,255,255,0.15)' : '#dbeafe'),
          color: isMe ? '#92400e' : (isMine ? 'white' : '#1e40af'),
        }}
        data-testid={`mention-pill-${id}`}
      >@{name}</span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) parts.push(body.slice(lastIndex));
  return parts.length ? parts : body;
}
